import { useSearchParams } from 'react-router-dom';

const DRAWER_PARAM = 'automation';
const MODE_PARAM = 'mode';
const EDIT_MODE = 'edit';
/** The param's value that opens the New Automation wizard. */
export const CREATE_VALUE = 'new';

/** The search string that opens a drawer, for links from outside the tab:
 * the detail drawer, the New Automation wizard, or (`edit`) the edit one. */
export function automationDrawerSearch(
  value: number | string,
  { edit = false } = {}
) {
  return `?${withDrawer(new URLSearchParams(), value, edit)}`;
}

/** `params` with the drawer set to `value` (or closed, for null) in edit
 * mode or not; other params are kept. */
function withDrawer(
  params: URLSearchParams,
  value: number | string | null,
  edit = false
) {
  const next = new URLSearchParams(params);
  next.delete(MODE_PARAM);
  if (value === null) next.delete(DRAWER_PARAM);
  else next.set(DRAWER_PARAM, String(value));
  if (edit) next.set(MODE_PARAM, EDIT_MODE);
  return next;
}

/** The drawers live in the URL, so they can be shared and the back button
 * closes them: `?automation=<id>` is the detail drawer, `?automation=new`
 * the New Automation wizard and `?automation=<id>&mode=edit` the Edit
 * Automation one. Other params (the table's page) are kept. Closing replaces
 * the entry, so Back after a close doesn't reopen one. */
export function useAutomationDrawer() {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(DRAWER_PARAM);
  const id = raw && Number.isFinite(Number(raw)) ? Number(raw) : null;
  const editing = searchParams.get(MODE_PARAM) === EDIT_MODE;
  /** The Automation the detail drawer shows. */
  const openId = editing ? null : id;
  /** The Automation the edit wizard edits. */
  const editingId = editing ? id : null;
  const isCreating = raw === CREATE_VALUE;

  /** The search string that opens the drawer on this Automation. */
  const drawerSearch = (id: number) => `?${withDrawer(searchParams, id)}`;

  /** The search string that opens the edit wizard on this Automation. */
  const editSearch = (id: number) => `?${withDrawer(searchParams, id, true)}`;

  /** The search string that opens the New Automation wizard. */
  const createSearch = `?${withDrawer(searchParams, CREATE_VALUE)}`;

  const close = () =>
    setSearchParams(withDrawer(searchParams, null), { replace: true });

  return {
    openId,
    editingId,
    isCreating,
    drawerSearch,
    editSearch,
    createSearch,
    close,
  };
}
